import React, { useState, useRef } from 'react';
import { Calendar, DollarSign, Building2, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePopper } from 'react-popper';
import { CustomerBudgetChart } from './charts/CustomerBudgetChart';
import { Customer } from '../types';

interface CustomerCardProps {
  customer: Customer & {
    budgetData: {
      year: string;
      confirmed: number;
      projected: number;
      weighted: number;
      minimum: number;
    }[];
    totalBudget: number;
    activeProjects: number;
    projectNames?: string[];
  };
  timeframe: {
    start: string;
    end: string;
    ticks: string[];
  };
}

export function CustomerCard({ customer, timeframe }: CustomerCardProps) {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'top',
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      { name: 'offset', options: { offset: [0, 8] } }
    ],
  });

  const formatBudget = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {customer.name}
            </h2>
            <div className="space-y-2">
              {customer.year_started && (
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>Since {customer.year_started}</span>
                </div>
              )}
              <div className="flex items-center text-gray-600">
                <DollarSign className="w-4 h-4 mr-2" />
                <span>{formatBudget(customer.totalBudget)}</span>
              </div>
              <div 
                ref={setReferenceElement}
                onMouseEnter={() => setTooltipVisible(true)}
                onMouseLeave={() => setTooltipVisible(false)}
              >
                <Link 
                  to={`/projects?customer=${encodeURIComponent(customer.name)}`}
                  className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  <span>{customer.activeProjects} projects</span>
                </Link>
              </div>
              {(customer.contacts || []).length > 0 && (
                <div className="flex items-center text-gray-600">
                  <Mail className="w-4 h-4 mr-2" />
                  <span>{customer.contacts.length} contacts</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <CustomerBudgetChart
          customerId={customer.id}
          data={customer.budgetData}
          timeframe={timeframe}
          formatBudget={formatBudget}
        />

        {tooltipVisible && customer.projectNames && customer.projectNames.length > 0 && (
          <div
            ref={setPopperElement}
            style={styles.popper}
            {...attributes.popper}
            className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-50 max-w-xs"
          >
            <div ref={setArrowElement} style={styles.arrow} />
            <div className="text-sm">
              <div className="font-medium text-gray-900 mb-2">Projects:</div>
              <ul className="space-y-1">
                {customer.projectNames.map((name, index) => (
                  <li key={index} className="text-gray-600">{name}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}